import React, { useEffect, useMemo, useState } from 'react';
import { Button, Icon, Input, Loader, Message, Modal, Select, Table, SemanticCOLORS, SemanticICONS } from 'semantic-ui-react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useToasts } from 'common/toast';
import { AccountRead } from '../../../../store/generated/admin/account';
import { accountApi } from '../api/accountApi';
import { beginImpersonation } from '../../../../common/utils/impersonationFlow';
import {
  ACCOUNT_ROLE,
  getCurrentAccount,
  getCurrentOrganization,
  getOrganizations,
  isCurrentUserStaffOrSuperAdmin,
} from '../../../../common/selectors/auth';
import { AppDispatch } from '../../../../store/store';
import PositionedModal from 'common/components/PositionedModal';

interface Organization {
  id: number;
  name: string;
}

interface ImpersonationPickerModalProps {
  open: boolean;
  onClose: () => void;
}

type AccountReadWithAccessLevel = AccountRead & {
  access_level?: string | null;
};

function getAccessLevelIcon(accessLevel?: string | null) {
  const normalized = (accessLevel ?? 'Member').trim().toLowerCase();

  if (normalized === 'organization admin') {
    return { icon: 'shield' as SemanticICONS, color: 'green' as SemanticCOLORS, title: 'Organization Admin' };
  }

  if (normalized === 'team leader') {
    return { icon: 'users' as SemanticICONS, color: 'blue' as SemanticCOLORS, title: 'Team Leader' };
  }

  return { icon: 'user' as SemanticICONS, color: 'grey' as SemanticCOLORS, title: 'Member' };
}

const ImpersonationPickerModal: React.FC<ImpersonationPickerModalProps> = ({
  open,
  onClose,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { addToast } = useToasts();
  const currentAccount = useSelector(getCurrentAccount);
  const currentOrganization = useSelector(getCurrentOrganization);
  const organizations = useSelector(getOrganizations) as Organization[] | undefined;
  const isStaffOrSuperAdmin = useSelector(isCurrentUserStaffOrSuperAdmin);
  const [searchTerm, setSearchTerm] = useState('');
  const [organizationId, setOrganizationId] = useState<number>(currentOrganization?.id ?? 0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setOrganizationId(currentOrganization?.id ?? 0);
    }
    // Only initialize the selected organization when the dialog opens.
    // Do not re-sync while the dialog remains open so the user can change it.
  }, [open]);

  const shouldLoadAccounts = open && isStaffOrSuperAdmin;
  const apiArg = {
    id: 0,
    ...(organizationId && organizationId > 0 ? { organization_id: organizationId } : {}),
  };
  const { data: accountsData, isLoading, error } =
    accountApi.useApiAdminV1AccountsListQuery(apiArg, { skip: !shouldLoadAccounts });

  const eligibleAccounts = useMemo(() => {
    if (!accountsData) return [];

    const normalizedSearch = searchTerm.trim().toLowerCase();

    return accountsData
      .filter((account: AccountReadWithAccessLevel) =>
        account.id !== currentAccount?.id &&
        account.role !== ACCOUNT_ROLE.SUPERUSER &&
        account.role !== ACCOUNT_ROLE.STAFF &&
        Boolean(account.is_active),
      )
      .filter((account: AccountReadWithAccessLevel) => {
        if (!normalizedSearch) return true;

        return [
          account.first_name,
          account.last_name,
          account.email,
        ]
          .filter(Boolean)
          .some(value => value!.toLowerCase().includes(normalizedSearch));
      })
      .sort((a, b) => {
        const lastNameA = (a.last_name ?? '').toLowerCase();
        const lastNameB = (b.last_name ?? '').toLowerCase();
        if (lastNameA < lastNameB) return -1;
        if (lastNameA > lastNameB) return 1;
        const firstNameA = (a.first_name ?? '').toLowerCase();
        const firstNameB = (b.first_name ?? '').toLowerCase();
        return firstNameA < firstNameB ? -1 : firstNameA > firstNameB ? 1 : 0;
      });
  }, [accountsData, currentAccount, searchTerm]);

  const handleBeginImpersonation = async (accountId: number) => {
    setIsSubmitting(true);
    try {
      await beginImpersonation(dispatch, navigate, accountId, '');
      onClose();
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Failed to start impersonation.', {
        appearance: 'error',
        autoDismiss: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const tableCellStyle = {
    borderLeft: 'none',
    borderRight: 'none',
  };

  return (
    <PositionedModal open={open} onClose={onClose} size="small">
      <Modal.Header style={{ fontFamily: "'Poppins', sans-serif" }}>Impersonate User</Modal.Header>
      <Modal.Content style={{ fontFamily: "'Poppins', sans-serif", maxHeight: '55vh', minHeight: '360px', overflow: 'hidden', paddingBottom: 0 }}>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', marginBottom: '1rem' }}>
          <Select
            fluid
            search
            options={
              [{ key: 'all', text: 'All Organizations', value: '0' }]
                .concat(
                  [...(organizations ?? [])]
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((org) => ({
                      key: String(org.id),
                      text: org.name,
                      value: String(org.id),
                    }))
                )
            }
            value={String(organizationId)}
            placeholder={currentOrganization?.name ?? 'Organization'}
            onChange={(_, data) => setOrganizationId(Number(data.value))}
            style={{ minWidth: 0, flex: 1 }}
            disabled={!organizations?.length}
          />
          <Input
            fluid
            icon="search"
            iconPosition="left"
            placeholder="Search by name or email"
            value={searchTerm}
            onChange={(_, data) => setSearchTerm(data.value as string)}
            style={{ minWidth: 0, flex: 1 }}
          />
        </div>

        <div style={{ maxHeight: 'calc(55vh - 160px)', minHeight: '220px', overflowY: 'auto' }}>
          {isLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '220px' }}>
              <Loader active inline="centered" size="small" />
            </div>
          ) : error ? (
            <Message negative content="Unable to load users. Please try again." />
          ) : (
            <Table celled selectable unstackable style={{ border: '1px solid rgba(34,36,38,.15)', borderCollapse: 'collapse' }}>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell style={tableCellStyle}>Name</Table.HeaderCell>
                  <Table.HeaderCell style={tableCellStyle}>Email</Table.HeaderCell>
                  <Table.HeaderCell style={tableCellStyle}>Access Level</Table.HeaderCell>
                  <Table.HeaderCell collapsing style={tableCellStyle} />
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {eligibleAccounts.length === 0 ? (
                  <Table.Row>
                    <Table.Cell colSpan={4} style={tableCellStyle}>
                      <Message content="No matching accounts found." />
                    </Table.Cell>
                  </Table.Row>
                ) : (
                  eligibleAccounts.map((account: AccountReadWithAccessLevel) => {
                    const accessLevel = account.access_level ?? (organizationId > 0 ? 'Member' : 'Member');
                    const accessIcon = getAccessLevelIcon(accessLevel);

                    return (
                      <Table.Row
                        key={account.id}
                        onClick={() => !isSubmitting && handleBeginImpersonation(account.id)}
                        style={{ cursor: isSubmitting ? 'default' : 'pointer' }}
                      >
                        <Table.Cell style={tableCellStyle}>
                          <Icon
                            name={accessIcon.icon}
                            color={accessIcon.color}
                            title={accessIcon.title}
                            style={{ marginRight: '0.5rem' }}
                          />
                          <span style={{ fontWeight: 600, fontSize: '0.95rem' }}>
                            {account.last_name}, {account.first_name}
                          </span>
                        </Table.Cell>
                        <Table.Cell style={tableCellStyle}>
                          <span style={{ color: 'rgba(0,0,0,0.65)', fontSize: '0.9rem' }}>
                            {account.email}
                          </span>
                        </Table.Cell>
                        <Table.Cell style={tableCellStyle}>
                          <span style={{ color: 'rgba(0,0,0,0.65)', fontSize: '0.9rem' }}>
                            {accessLevel}
                          </span>
                        </Table.Cell>
                        <Table.Cell collapsing textAlign="center" style={tableCellStyle}>
                          <Icon name="chevron right" />
                        </Table.Cell>
                      </Table.Row>
                    );
                  })
                )}
              </Table.Body>
            </Table>
          )}
        </div>
      </Modal.Content>
      <Modal.Actions>
        <Button color="grey" onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
      </Modal.Actions>
    </PositionedModal>
  );
};

export default ImpersonationPickerModal;
